import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { PartyPopper, RefreshCw } from 'lucide-react'
import { apiFetch } from '../../config/api'
import { type PageStat, StatTable } from './statistikShared'

export default function DashboardStatistikPartys() {
  const [pages, setPages] = useState<PageStat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const getAuthToken = () => localStorage.getItem('dashboard_token') || ''

  const fetchStats = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiFetch('/analytics/pages', {
        headers: { Authorization: getAuthToken() },
      })
      if (!res.ok) {
        if (res.status === 401) return
        throw new Error('Statistiken konnten nicht geladen werden.')
      }
      const data = await res.json()
      if (data.success && Array.isArray(data.pages)) {
        const all = data.pages.map((p: { path: string; today: string | number; last7d: string | number }) => ({
          path: p.path || '/',
          today: String(p.today ?? 0),
          last7d: String(p.last7d ?? 0),
        }))
        setPages(all.filter((p: PageStat) => (p.path || '').startsWith('/partys')))
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  const partysToday = pages.reduce((s, p) => s + (parseInt(p.today, 10) || 0), 0)
  const partys7d = pages.reduce((s, p) => s + (parseInt(p.last7d, 10) || 0), 0)
  const max7dPartys = Math.max(1, ...pages.map((p) => parseInt(p.last7d, 10) || 0))

  return (
    <div className="w-full min-w-0 max-w-full">
      <motion.div
        className="mb-6 sm:mb-8 md:mb-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-950">
              Events & Partys
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Wie oft werden Partys und Event-Seiten angeklickt?
            </p>
          </div>
          <motion.button
            onClick={fetchStats}
            disabled={loading}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </motion.button>
        </div>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {error}
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-xl sm:rounded-xl bg-white border border-zinc-200 overflow-hidden min-w-0"
      >
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-zinc-200 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-3 sm:gap-4 bg-zinc-50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-zinc-100 border border-zinc-200">
              <PartyPopper className="w-5 h-5 text-zinc-700" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-zinc-950">Partys & Events</h2>
              <p className="text-zinc-500 text-xs sm:text-sm">Aufrufe pro Seite (Übersicht und Event-Details)</p>
            </div>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="text-right">
              <p className="text-xl sm:text-2xl font-bold text-zinc-950 tabular-nums">{loading ? '—' : partysToday}</p>
              <p className="text-xs text-zinc-500">Heute</p>
            </div>
            <div className="w-px h-8 sm:h-10 bg-zinc-100" />
            <div className="text-right">
              <p className="text-xl sm:text-2xl font-bold text-zinc-700 tabular-nums">{loading ? '—' : partys7d}</p>
              <p className="text-xs text-zinc-500">7 Tage</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 sm:p-12 text-center text-zinc-500">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 opacity-70" />
            <p>Statistiken werden geladen …</p>
          </div>
        ) : pages.length === 0 ? (
          <div className="px-4 sm:px-6 py-6 sm:py-8 text-center text-gray-500 text-sm">
            Noch keine Aufrufe von Party-Seiten.
          </div>
        ) : (
          <div className="overflow-x-auto min-w-0 w-full">
            <StatTable rows={pages} maxBar={max7dPartys} startIndex={0} />
          </div>
        )}
      </motion.div>
    </div>
  )
}
